// class for RfxResponse

import { DateTime } from 'luxon';
import pick from 'lodash/pick';

// make other classes for related/nested things!

class Rfx {
    constructor({  // you can set defaults for constructor params here, or just let them be undefined and set in constructor method
        id,
        pk,  // for old times sake
        created,
        modified,
        is_active,
        title,
        text_content,
        sid,
        tenant_id,
        rfx_type,
        rfx_type_display,
        rfx_type_options,
        allow_late_responses,
        allow_late_modify,
        // OLD STATE/STATUS stuff
        // rfx_status,
        // rfx_status_object,  // temporary, while we move to state machine
        // rfx_status_options,  // temporary, while we move to state machine
        // allowed_rfx_status_options,  // temporary, while we move to state machine
        // end of old status stuff
        open_for_invites,  // may want to keep?  maybe new name?  since you now have 'can_send_invites' and 'can_create_invites' separately
        can_create_invites,
        can_send_invites,  // you may be able to create them but not send (if in draft state)
        // NEW STATE/STATUS stuff
        sm_state,
        last_sm_state,
        state_dict,
        state_options,
        allowed_state_options,  // may not need long term... only use possible_transitions instead?
        possible_transitions,
        // possible_states,  // can get this from possible_transitions?
        open_date_actual,
        open_date_planned,
        open_date_to_show,
        close_date,  // deprecate?
        close_date_actual,
        close_date_planned,
        close_date_to_show,
        question_close_date,
        disable_auto_dates,
        is_confidential,
        rfx_tenant_users,
        company_todos,
        response_counts,  // dict/obj of state: count
        vendor_names_by_state,  // dict/obj with keys for each state, with list of vendor names in each
        invited_count,
        submitted_count,
        awarded_count, 
        awarded_responses,  // array of RfxResponse objects - just set as is... don't make nested class instances for now
        responses_to_remind_count,
        go_live_status,  // obj with 'ready' and 'errors' keys - errors is dict
        show_real_owner_email,
        sourcing_owner_id,
        sourcing_owner,
        sourcing_owner_name,  // in case we only want name, and hide the rest
        sourcing_owner_email,  // will be empty if show_real_owner_email is false
        sourcing_owner_options,
        sourcing_category_id,
        sourcing_category,
        sourcing_category_options,
        sourcing_tags,
        portal_listing_enabled,
        public_listing_enabled,
        quicklinks_enabled,
        is_quicklink_active,  // are quicklinks actually working right now based on state, tenant settings, rfx setting, etc
        criteria_req_enabled,
        criteria_scoring_enabled,
        criteria_show_vendors,
        has_existing_score_sheets,
        criteria_list,
        bulk_messages,
        files,
        spend_baseline,
        spend_target,
        response_instructions,
        invite_email_text,
        internal_url,
        vendor_url,
        internal_manage_url,
        internal_setup_url,
        manage_invites_url,
        can_tu_view,  // Boolean, from model method .can_tu_view_rfx(tu)
        latest_submission_date,  // DateTime, from qs annotation `latest_submission_date`
        has_unread_external_messages,  // Boolean, from qs annotation `has_unread_external_messages` - for current TU
        has_unread_internal_messages,  // Boolean, from qs annotation `has_unread_internal_messages` - for current TU
        latest_external_message_date, // DateTime, from qs annotation `last_external_message_date` - NOT TU specific
        latest_internal_message_date,  // DateTime, from qs annotation `last_internal_message_date` - NOT TU specific
    }) {
        // make sure to set defaults if they won't always be there, and undefined isn't cool!
        this.id = id ?? pk;
        this.pk = pk ?? id;
        this.sid = sid ?? '';
        this.created = created ? DateTime.fromISO(created) : null;
        this.modified = modified ? DateTime.fromISO(modified) : null;
        this.tenant_id = tenant_id ?? '';
        this.is_active = is_active;
        this.title = title ?? '';
        this.text_content = text_content ?? '';
        this.rfx_type = rfx_type ?? '';
        this.rfx_type_display = rfx_type_display ?? '';
        this.rfx_type_options = rfx_type_options ?? [];
        this.allow_late_responses = allow_late_responses;
        this.allow_late_modify = allow_late_modify;
        // new state / status stuff
        this.sm_state = sm_state ?? '';
        this.last_sm_state = last_sm_state ?? '';
        this.state_dict = state_dict ?? {};
        this.state_options = state_options ?? [];
        this.allowed_state_options = allowed_state_options ?? [];
        this.possible_transitions = possible_transitions ?? [];
        // old status / state stuff - remove later
        // this.rfx_status = rfx_status ?? '';
        // this.rfx_status_object = rfx_status_object ?? state_dict ?? {};
        // this.rfx_status_options = rfx_status_options ?? [];
        // this.allowed_rfx_status_options = allowed_rfx_status_options ?? [];
        //// end of old status stuff
        this.open_for_invites = open_for_invites;
        this.can_create_invites = can_create_invites;
        this.can_send_invites = can_send_invites;
        this.open_date_actual = open_date_actual ? DateTime.fromISO(open_date_actual) : null;
        this.open_date_planned = open_date_planned ? DateTime.fromISO(open_date_planned) : null;
        this.open_date_to_show = open_date_to_show ? DateTime.fromISO(open_date_to_show) : null;
        this.close_date = close_date ? DateTime.fromISO(close_date) : null;  // DEPRECATE???
        this.close_date_actual = close_date_actual ? DateTime.fromISO(close_date_actual) : null;
        this.close_date_planned = close_date_planned ? DateTime.fromISO(close_date_planned) : null;
        this.close_date_to_show = close_date_to_show ? DateTime.fromISO(close_date_to_show) : null;
        this.question_close_date = question_close_date ? DateTime.fromISO(question_close_date) : null;
        this.disable_auto_dates = disable_auto_dates;
        this.is_confidential = is_confidential;
        this.rfx_tenant_users = rfx_tenant_users ?? [];
        this.company_todos = company_todos ?? [];
        this.response_counts = response_counts ?? {};  // object with keys for each state and some general ones
        this.vendor_names_by_state = vendor_names_by_state ?? {};
        this.invited_count = invited_count ?? null;
        this.submitted_count = submitted_count ?? null;
        this.awarded_count = awarded_count ?? null;
        this.awarded_responses = awarded_responses ?? [];
        this.responses_to_remind_count = responses_to_remind_count ?? null;
        this.go_live_status = go_live_status ?? {};  // keys are 'ready' and 'errors' (a dict of errors)
        this.show_real_owner_email = show_real_owner_email;
        this.sourcing_owner = sourcing_owner ?? {};
        this.sourcing_owner_id = sourcing_owner_id ?? this.sourcing_owner?.id ?? '';
        this.sourcing_owner_name = sourcing_owner_name ?? this.sourcing_owner?.full_name ?? '';
        this.sourcing_owner_email = sourcing_owner_email ?? this.sourcing_owner?.email ?? '';  // we can autopopulate from sourcing_owner object, because that will be empty if this is empty too...
        this.sourcing_owner_options = sourcing_owner_options ?? [];
        this.sourcing_category = sourcing_category ?? {};
        this.sourcing_category_id = sourcing_category_id ?? sourcing_category?.id ?? '';
        this.sourcing_category_name = sourcing_category?.name ?? '';
        this.sourcing_category_options = sourcing_category_options ?? [];
        this.sourcing_tags = sourcing_tags ?? [];
        this.portal_listing_enabled = portal_listing_enabled;
        this.public_listing_enabled = public_listing_enabled;
        this.quicklinks_enabled = quicklinks_enabled;
        this.is_quicklink_active = is_quicklink_active;   // are quicklinks actually working right now based on state, tenant settings, rfx setting, etc
        this.criteria_req_enabled = criteria_req_enabled;
        this.criteria_scoring_enabled = criteria_scoring_enabled;
        this.criteria_show_vendors = criteria_show_vendors;
        this.has_existing_score_sheets = has_existing_score_sheets;
        this.criteria_list = criteria_list ?? [];
        this.bulk_messages = bulk_messages ?? [];
        this.files = files ?? [];
        this.spend_baseline = spend_baseline ?? null;
        this.spend_target = spend_target ?? null;
        this.response_instructions = response_instructions ?? '';
        this.invite_email_text = invite_email_text ?? '';
        this.internal_url = internal_url ?? '';
        this.internal_manage_url = internal_manage_url ?? '';
        this.internal_setup_url = internal_setup_url ?? '';
        this.vendor_url = vendor_url ?? '';
        this.manage_invites_url = manage_invites_url ?? '';
        this.can_tu_view = can_tu_view;  // maybe fallback to checking other things if we don't have this field?
        this.latest_submission_date = latest_submission_date ? DateTime.fromISO(latest_submission_date) : null;
        this.has_unread_external_messages = has_unread_external_messages ?? false;
        this.has_unread_internal_messages = has_unread_internal_messages ?? false;
        this.has_any_unread_messages = this.has_unread_external_messages || this.has_unread_internal_messages;
        this.latest_external_message_date = latest_external_message_date ? DateTime.fromISO(latest_external_message_date) : null;
        this.latest_internal_message_date = latest_internal_message_date ? DateTime.fromISO(latest_internal_message_date) : null;
        // new things - frontend router links, etc.?
        //this.profileRouterLink = this.makeProfileRouterLink();  // string or object
        //this.manageRouterLink = this.makeManageRouterLink();  // string or object
        //this.adminRouterLink = this.makeAdminRouterLink();
    }
    // GETTERS
    // getters don't work in Vue as you expect.... don't use them
    // METHODS
    // TO ADD
    //is_submitted() {}
    //is_invite() {}
    //can_receive_reminder() {}
    //can_receive_invite() {}
    formatForApi({ fields }) {
        // put things in correct format for POST/PUT/PATCH
        // only return fields including in 'fields' Array you pass in params
        // KEY POINT: return FKs/M2Ms as their IDs, not as full objects?... hmmm, will totally depend on what your endpoint expects...
        // ALSO - turn DateTime's back into ISO strings?
        // come back to this later...
        return pick(this.sourcing_category, fields);
    }
    //admin_tu_ids() {}  // get things from RFX / team, etc.
}


export {
    Rfx,
}